/**
 * Google Ads / gtag.js event payloads.
 * @see https://developers.google.com/tag-platform/gtagjs
 * @see https://developers.google.com/tag-platform/gtagjs/reference/shopify-event-parameters
 */
import {
    ApiShopperLandingPageLink,
    centsToCurrencyUnits,
} from '@storefronts/common-types'
import { Cart, VariantWithImage } from '../types'
import { runWhenReady } from '../components/pixels/pixelRuntime'

const CURRENCY = 'USD'

type GoogleAdsTagId = string | null | undefined

interface GoogleAdsItem {
    item_id: string
    item_name?: string
    price?: number
    quantity?: number
}

interface GoogleAdsEventData {
    send_to: string
    currency?: string
    value?: number
    items?: GoogleAdsItem[]
}

export class GoogleAdsTracker {
    private static isGtagAvailable(): boolean {
        return (
            typeof window !== 'undefined' && typeof window.gtag === 'function'
        )
    }

    // `window.gtag`/`window.dataLayer` are shared with any other configured
    // Google tag (e.g. GA4). `send_to` scopes each event to this Google Ads
    // tag specifically so it isn't also recorded against other configured
    // tags.
    private static gtagEvent(
        tagId: GoogleAdsTagId,
        eventName: string,
        data?: Omit<GoogleAdsEventData, 'send_to'>,
    ): void {
        runWhenReady('googleGtag', () => {
            if (!tagId || !GoogleAdsTracker.isGtagAvailable()) return
            window.gtag('event', eventName, { ...data, send_to: tagId })
        })
    }

    private static linkToItem(
        link: ApiShopperLandingPageLink,
        quantity = 1,
    ): GoogleAdsItem {
        return {
            item_id: link.shopifyId,
            item_name: link.title,
            price: centsToCurrencyUnits(link.price.price),
            quantity,
        }
    }

    private static variantToItem(
        variant: VariantWithImage,
        quantity: number,
    ): GoogleAdsItem {
        return {
            item_id: variant.link.shopifyId,
            item_name: variant.link.title,
            price: centsToCurrencyUnits(variant.finalPrice),
            quantity,
        }
    }

    private static cartToItems(cart: Cart): GoogleAdsItem[] {
        return cart.items.map(({ variant, quantity }) =>
            GoogleAdsTracker.variantToItem(variant, quantity),
        )
    }

    private static calculateItemsValue(items: GoogleAdsItem[]): number {
        const total = items.reduce(
            (sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1),
            0,
        )
        return Math.round(total * 100) / 100
    }

    private static trackItemsEvent(
        tagId: GoogleAdsTagId,
        eventName: string,
        items: GoogleAdsItem[],
    ): void {
        GoogleAdsTracker.gtagEvent(tagId, eventName, {
            currency: CURRENCY,
            value: GoogleAdsTracker.calculateItemsValue(items),
            items,
        })
    }

    static trackPageView(tagId: GoogleAdsTagId): void {
        GoogleAdsTracker.gtagEvent(tagId, 'page_view')
    }

    static trackViewItem(
        tagId: GoogleAdsTagId,
        product: ApiShopperLandingPageLink,
    ): void {
        GoogleAdsTracker.trackItemsEvent(tagId, 'view_item', [
            GoogleAdsTracker.linkToItem(product),
        ])
    }

    static trackAddToCart(
        tagId: GoogleAdsTagId,
        product: VariantWithImage,
        quantity: number,
    ): void {
        GoogleAdsTracker.trackItemsEvent(tagId, 'add_to_cart', [
            GoogleAdsTracker.variantToItem(product, quantity),
        ])
    }

    static trackBeginCheckout(
        tagId: GoogleAdsTagId,
        options?: { cart?: Cart; buyNowVariant?: VariantWithImage },
    ): void {
        if (options?.cart && options.cart.items.length > 0) {
            GoogleAdsTracker.trackItemsEvent(
                tagId,
                'begin_checkout',
                GoogleAdsTracker.cartToItems(options.cart),
            )
            return
        }
        if (options?.buyNowVariant) {
            GoogleAdsTracker.trackItemsEvent(tagId, 'begin_checkout', [
                GoogleAdsTracker.variantToItem(options.buyNowVariant, 1),
            ])
        }
    }
}
